import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { Input } from "@andorhealth/clientuireact.designsystem";
import {
  ConciergeChecking,
  ConciergeUnavailable,
} from "@/components/ConciergeUnavailable";
import { StaffAppHeader } from "@/components/StaffAppHeader";
import { AuthenticatedAppShell } from "@/components/auth/AuthGate";
import { useAuth } from "@/lib/auth/AuthContext";
import { setAuthReturnPath } from "@/lib/auth/authService";
import { fetchAgentConfig } from "@/lib/elevenlabs/agent";
import { fetchDevices, type ManagementDevice } from "@/lib/management-api";

const PAGE_SIZE = 20;

function looksLikeGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );
}

function DeviceRow({
  device,
  onSelect,
}: {
  device: ManagementDevice;
  onSelect: () => void;
}) {
  const name = device.deviceName?.trim() || "";
  const alias = device.deviceAlias?.trim() || "";
  // Prefer a human alias as the title when it isn't just a GUID.
  const title =
    (alias && !looksLikeGuid(alias) ? alias : null) ||
    name ||
    alias ||
    "Unnamed device";
  const secondaryId =
    alias && alias !== title
      ? alias
      : name && name !== title
        ? name
        : null;

  const room = device.room?.trim();
  const bed = device.bed?.trim();
  const roomBed = [room ? `Room ${room}` : null, bed ? `Bed ${bed}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns: "36px minmax(0, 1fr) auto auto",
        alignItems: "center",
        columnGap: 1.5,
        width: "100%",
        boxSizing: "border-box",
        px: 2,
        py: 1.25,
        borderBottom: "1px solid",
        borderColor: "divider",
        transition: "background-color 120ms ease",
        "&:hover": { bgcolor: "rgba(15, 76, 129, 0.05)" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(15, 76, 129, 0.08)",
          color: "primary.main",
          borderRadius: "2px",
        }}
      >
        <TvOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.975rem",
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {secondaryId ? (
          <Typography
            sx={{
              mt: 0.15,
              color: "text.secondary",
              fontSize: "0.75rem",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {secondaryId}
          </Typography>
        ) : null}
      </Box>

      {roomBed ? (
        <Typography
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "primary.main",
            bgcolor: "rgba(15, 76, 129, 0.08)",
            px: 0.9,
            py: 0.35,
            borderRadius: "2px",
            lineHeight: 1.3,
            whiteSpace: "nowrap",
          }}
        >
          {roomBed}
        </Typography>
      ) : (
        <span />
      )}

      <ChevronRightIcon
        sx={{ color: "text.secondary", flexShrink: 0, opacity: 0.55, fontSize: 22 }}
      />
    </Box>
  );
}

function DevicePickerInner() {
  const { userProfile, isLoading: authLoading } = useAuth();
  const tenantKey = userProfile?.profile.tenantKey?.trim() || "";

  const [tenantReady, setTenantReady] = useState<"loading" | "ready" | "unavailable">(
    "loading",
  );
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<ManagementDevice[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (authLoading) {
      setTenantReady("loading");
      return;
    }
    if (!tenantKey) {
      setTenantReady("unavailable");
      return;
    }

    let cancelled = false;
    setTenantReady("loading");
    void (async () => {
      const config = await fetchAgentConfig({
        tenantKey,
        deviceId: null,
        loggedIn: true,
      });
      if (cancelled) return;
      setTenantReady(config ? "ready" : "unavailable");
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantKey, authLoading]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (tenantReady !== "ready") return;
    setLoading(true);
    setError(null);
    const result = await fetchDevices({
      tenantKey,
      search: debounced || undefined,
      page,
      pageSize: PAGE_SIZE,
    });
    setLoading(false);
    if (!result.ok) {
      setError(result.message || "Unable to load devices");
      setDevices([]);
      setTotalCount(0);
      return;
    }
    setDevices(result.devices);
    setTotalCount(result.totalCount);
    if (result.devices.length > PAGE_SIZE) {
      setDevices(result.devices.slice(0, PAGE_SIZE));
    }
  }, [tenantKey, debounced, page, tenantReady]);

  useEffect(() => {
    void load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const selectDevice = (deviceId: string) => {
    const params = new URLSearchParams();
    params.set("deviceId", deviceId);
    params.set("tenantKey", tenantKey);
    window.location.href = `/?${params.toString()}`;
  };

  if (tenantReady === "loading") {
    return <ConciergeChecking />;
  }

  if (tenantReady === "unavailable") {
    return <ConciergeUnavailable />;
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        background: `
          radial-gradient(ellipse 70% 50% at 50% 0%, rgba(15, 76, 129, 0.08), transparent 55%),
          linear-gradient(180deg, #f3f5f8 0%, #e8ecf1 100%)
        `,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, md: 4, xl: 5 },
          maxWidth: 960,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          py: { xs: 3, md: 4 },
          gap: 2.5,
        }}
      >
        <StaffAppHeader title="Select a device" active="board" />

        <Input
          fullWidth
          size="small"
          placeholder="Search by name, alias, room…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<SearchIcon sx={{ color: "text.secondary" }} />}
          iconPosition="start"
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          {error ? (
            <Typography sx={{ color: "error.main", p: 3, flexShrink: 0 }}>
              {error}
            </Typography>
          ) : null}

          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", flex: 1, py: 8 }}>
              <CircularProgress size={36} sx={{ color: "primary.main" }} />
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {devices.map((d) => (
                  <DeviceRow
                    key={d.deviceId}
                    device={d}
                    onSelect={() => selectDevice(d.deviceId)}
                  />
                ))}
                {!devices.length ? (
                  <Typography
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                      py: 10,
                      fontWeight: 600,
                    }}
                  >
                    No devices match this search.
                  </Typography>
                ) : null}
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  px: 2.25,
                  py: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "rgba(232, 236, 241, 0.55)",
                }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                  {totalCount === 0
                    ? "0 devices"
                    : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount}`}
                </Typography>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, next) => setPage(next)}
                  color="primary"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                  disabled={totalCount === 0}
                />
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}

/** Login required, then pick a DeviceId; tenant comes from the signed-in profile. */
export function AgentDeviceSetup() {
  useEffect(() => {
    setAuthReturnPath("/");
  }, []);

  return (
    <AuthenticatedAppShell>
      <DevicePickerInner />
    </AuthenticatedAppShell>
  );
}
